import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

export type AlertProfileType =
  | "ground_settlement"
  | "building_settlement"
  | "utility_settlement"
  | "ground_water"
  | "crack"
  | "tilting"
  | "vibration"
  | "angular_distortion_building"
  | "angular_distortion_utility"
  | "movement"
  | "movement_settlement"
  | "movement_lateral";

export interface AlertLevel {
  order: number;
  name: string;
  value: number;
  entry_mode?: "value" | "gradient";
}

export interface VibrationAlertLevelJson {
  [key: string]: AlertLevel[];
  tran_rs: AlertLevel[];
  cont_rs: AlertLevel[];
  tran_sd: AlertLevel[];
  cont_sd: AlertLevel[];
}

export interface AlertProfileDevice {
  id: string;
  device_name: string;
}

export interface AlertProfile {
  id: number;
  project_id: number;
  description: string;
  profile_type: AlertProfileType;
  alert_level_json: AlertLevel[] | VibrationAlertLevelJson;
  updated_at?: string;
  cretaed_at?: string;
  devices: AlertProfileDevice[];
}

export interface AlertProfileInput {
  project_id: number;
  description: string;
  profile_type: AlertProfileType;
  alert_level_json: AlertLevel[] | VibrationAlertLevelJson;
}

export const alertProfilesApi = createApi({
  reducerPath: "alertProfilesApi",
  baseQuery: fetchBaseQuery({ baseUrl: process.env.REACT_APP_API_URL }),
  tagTypes: ["AlertProfile"],
  endpoints: (builder) => ({
    getAlertProfiles: builder.query<AlertProfile[], number | void>({
      query: (projectId) => {
        const sp = new URLSearchParams();
        if (projectId != null) sp.append("project_id", String(projectId));
        return { url: `alert-profiles?${sp.toString()}` };
      },
      providesTags: ["AlertProfile"],
    }),
    createAlertProfile: builder.mutation<AlertProfile, AlertProfileInput>({
      query: (body) => ({ url: "alert-profiles", method: "POST", body }),
      invalidatesTags: ["AlertProfile"],
    }),
    updateAlertProfile: builder.mutation<
      AlertProfile,
      { id: number; body: Partial<AlertProfileInput> }
    >({
      query: ({ id, body }) => ({
        url: `alert-profiles/${id}`,
        method: "PUT",
        body,
      }),
      // invalidatesTags: ["AlertProfile"],
    }),
    deleteAlertProfile: builder.mutation<void, number>({
      query: (id) => ({ url: `alert-profiles/${id}`, method: "DELETE" }),
      invalidatesTags: ["AlertProfile"],
    }),
    setProfileDevices: builder.mutation<
      void,
      { id: number; device_ids: string[] }
    >({
      query: ({ id, device_ids }) => ({
        url: `alert-profiles/${id}/devices`,
        method: "PUT",
        body: { device_ids },
      }),
      invalidatesTags: ["AlertProfile"],
    }),
    getAlertProfileDevices: builder.query<any[], number>({
      query: (id) => ({ url: `alert-profiles/${id}/devices` }),
    }),
  }),
});

export const {
  useGetAlertProfilesQuery,
  useCreateAlertProfileMutation,
  useUpdateAlertProfileMutation,
  useDeleteAlertProfileMutation,
  useSetProfileDevicesMutation,
  useGetAlertProfileDevicesQuery,
} = alertProfilesApi;
